import type { Answers, QuestionKey } from './constants';
import type { Gender } from '@/gql/graphql';
import { parse } from 'date-fns';

const createSelectValidationRules = <T extends Validatable>(
  questionName: string
): ValidationRules<Nullable<T>> => [
  {
    errorMessage: `${questionName}を入力してください。`,
    required: true,
  },
];

const create万円ValidationRules = (
  questionName: string
): ValidationRules<Nullable<string>> => [
  {
    errorMessage: `${questionName}を入力してください。`,
    required: true,
  },
  {
    errorMessage: '数字で入力してください。',
    validate(value) {
      if (value == null) return false;
      return /^\d+$/.test(value);
    },
  },
  {
    errorMessage: '100,000,000以下の数字で入力してください。',
    validate(value) {
      if (value == null) return false;
      return Number(value) <= 100_000_000;
    },
  },
];

const create円ValidationRules = (
  questionName: string
): ValidationRules<Nullable<string>> => [
  {
    errorMessage: `${questionName}を入力してください。`,
    required: true,
  },
  {
    errorMessage: '数字で入力してください。',
    validate(value) {
      if (value == null) return false;
      return /^\d+$/.test(value);
    },
  },
  {
    errorMessage: '1,000,000,000,000以下の数字で入力してください。',
    validate(value) {
      if (value == null) return false;
      return Number(value) <= 1_000_000_000_000;
    },
  },
  {
    errorMessage: '万円ではなく、円で入力してください',
    validate(value) {
      if (value == null) return false;
      const num = Number.parseInt(value, 10);
      if (num === 0) return true;
      return num >= 100;
    },
  },
];

const createBirthDayValidationRules = (
  questionName: string,
  isAdult: boolean = true
): ValidationRules<Nullable<string>> => {
  const baseRules: ValidationRules<Nullable<string>> = [
    {
      errorMessage: `${questionName}を入力してください。`,
      required: true,
    },
    {
      errorMessage: '数字8桁で入力してください。',
      validate(value) {
        if (value == null) return false;
        return /^\d{8}$/.test(value);
      },
    },
    {
      errorMessage: '未来の日程は入れられません',
      validate(value) {
        if (value == null) return false;
        if (!/^\d{8}$/.test(value)) return false;
        const birthday = parse(value, 'yyyyMMdd', new Date());
        return birthday <= new Date();
      },
    },
  ];
  if (isAdult) {
    return [
      ...baseRules,
      {
        errorMessage: '18歳から100歳の間で入力してください。',
        validate(value) {
          if (value == null) return false;
          if (!/^\d{8}$/.test(value)) return false;
          const birthday = parse(value, 'yyyyMMdd', new Date());
          const age = getAge(birthday, new Date());
          return age >= 18 && age <= 100;
        },
      },
    ];
  }
  return baseRules;
};

export const useDiagnosisValidation = (
  answers: Ref<Answers>,
  enabledQuestions: Ref<Record<QuestionKey, boolean>>
) => {
  const { fetchPostalCodeResult } = useFetchPostalCodeResult();

  return useValidation(
    answers,
    computed(() => {
      return {
        gender: enabledQuestions.value.gender
          ? createSelectValidationRules<Gender>('性別')
          : [],
        jobId: enabledQuestions.value.jobId
          ? createSelectValidationRules<string>('ご職業')
          : [],
        postalCode: enabledQuestions.value.postalCode
          ? [
              {
                errorMessage: '郵便番号を入力してください。',
                required: true,
              },
              {
                errorMessage: '郵便番号は7桁の半角数字で入力してください。',
                validate(value) {
                  if (value == null) return false;
                  return /^\d{7}$/.test(value);
                },
              },
              {
                errorMessage: '正しくない郵便番号です。',
                async validate(value) {
                  if (value == null) return false;
                  const result = await fetchPostalCodeResult(value);
                  if (result == null) return false;
                  return result[0] != undefined;
                },
              },
            ]
          : [],
        address: enabledQuestions.value.address
          ? [
              {
                errorMessage: '住所を選択してください。',
                required: true,
              },
            ]
          : [],
        birthday: enabledQuestions.value.birthday
          ? createBirthDayValidationRules('生年月日')
          : [],
        annualIncome: enabledQuestions.value.annualIncome
          ? create万円ValidationRules('年収')
          : [],
        savings: enabledQuestions.value.savings
          ? create万円ValidationRules('貯金額')
          : [],
        monthlySavings: enabledQuestions.value.monthlySavings
          ? create万円ValidationRules('毎月の貯金額')
          : [],
        investment: enabledQuestions.value.investment
          ? create万円ValidationRules('資産運用の合計額')
          : [],
        hasPartner: enabledQuestions.value.hasPartner
          ? createSelectValidationRules<boolean>('配偶者・パートナーの有無')
          : [],
        partnerBirthday: enabledQuestions.value.partnerBirthday
          ? createBirthDayValidationRules('配偶者・パートナーの生年月日')
          : [],
        partnerAnnualIncome: enabledQuestions.value.partnerAnnualIncome
          ? create万円ValidationRules('配偶者・パートナーの年収')
          : [],
        hasChild: enabledQuestions.value.hasChild
          ? createSelectValidationRules<boolean>('お子様の有無')
          : [],
        countOfChildren: enabledQuestions.value.countOfChildren
          ? createSelectValidationRules<1 | 2 | 3 | 4 | 5>('お子様の人数')
          : [],
        firstChildBirthDay: enabledQuestions.value.firstChildBirthDay
          ? createBirthDayValidationRules('1人目のお子様の生年月日', false)
          : [],
        secondChildBirthDay: enabledQuestions.value.secondChildBirthDay
          ? createBirthDayValidationRules('2人目のお子様の生年月日', false)
          : [],
        thirdChildBirthDay: enabledQuestions.value.thirdChildBirthDay
          ? createBirthDayValidationRules('3人目のお子様の生年月日', false)
          : [],
        fourthChildBirthDay: enabledQuestions.value.fourthChildBirthDay
          ? createBirthDayValidationRules('4人目のお子様の生年月日', false)
          : [],
        fifthChildBirthDay: enabledQuestions.value.fifthChildBirthDay
          ? createBirthDayValidationRules('5人目のお子様の生年月日', false)
          : [],
        firstChildGender: enabledQuestions.value.firstChildGender
          ? createSelectValidationRules<Gender>('1人目のお子様の性別')
          : [],
        secondChildGender: enabledQuestions.value.secondChildGender
          ? createSelectValidationRules<Gender>('2人目のお子様の性別')
          : [],
        thirdChildGender: enabledQuestions.value.thirdChildGender
          ? createSelectValidationRules<Gender>('3人目のお子様の性別')
          : [],
        fourthChildGender: enabledQuestions.value.fourthChildGender
          ? createSelectValidationRules<Gender>('4人目のお子様の性別')
          : [],
        fifthChildGender: enabledQuestions.value.fifthChildGender
          ? createSelectValidationRules<Gender>('5人目のお子様の性別')
          : [],
        monthlyRentCost: enabledQuestions.value.monthlyRentCost
          ? create万円ValidationRules('毎月の住宅費')
          : [],
        loanBalance: enabledQuestions.value.loanBalance
          ? create万円ValidationRules('住宅ローン、カーローン等の借入総額')
          : [],
        monthlyInsurance: enabledQuestions.value.monthlyInsurance
          ? create円ValidationRules('毎月の保険料')
          : [],
        thinksToughTimesBringOpportunity: enabledQuestions.value
          .thinksToughTimesBringOpportunity
          ? createSelectValidationRules<boolean>('ピンチはチャンスだと思うか')
          : [],
        thinksWhatDoneIsDone: enabledQuestions.value.thinksWhatDoneIsDone
          ? createSelectValidationRules<boolean>(
              '投資金額の30%を損しても耐えられるか'
            )
          : [],
        isInterestedInNisaOrIdeco: enabledQuestions.value
          .isInterestedInNisaOrIdeco
          ? createSelectValidationRules<boolean>('NISAやiDeCoに興味があるか')
          : [],
        hasGoalInFuture: enabledQuestions.value.hasGoalInFuture
          ? createSelectValidationRules<boolean>('将来の目標や夢があるか')
          : [],
        takesThreeOrMoreLessons: enabledQuestions.value.takesThreeOrMoreLessons
          ? createSelectValidationRules<boolean>('3つ以上の習い事をしているか')
          : [],
        followsTheLatestTrends: enabledQuestions.value.followsTheLatestTrends
          ? createSelectValidationRules<boolean>('流行には敏感か')
          : [],
        karteTopics: enabledQuestions.value.karteTopics
          ? [
              {
                errorMessage: '選択肢を1つ以上選択してください。',
                validate(value) {
                  return (value?.length ?? 0) >= 1;
                },
              },
            ]
          : [],
        karteComment: enabledQuestions.value.karteComment
          ? [
              {
                errorMessage: '15文字以上で入力してください。',
                validate(value) {
                  if (value == null) return false;
                  const whitespaceCharRemovedValue = value.replace(/\s/g, '');
                  return whitespaceCharRemovedValue.length >= 15;
                },
              },
            ]
          : [],
      };
    })
  );
};
